import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';

/**
 * useCarouselArrows
 * -----------------
 * Drives "is there anywhere to scroll, and where am I in the scroll range"
 * for a react-slick carousel by measuring the actual DOM, rather than by
 * counting items against a hard-coded threshold (e.g. `items.length > 4`).
 *
 * Why measure instead of count?
 *   Most carousels in this codebase use `variableWidth: true`, so the number
 *   of items that fit per row depends on viewport width and per-card sizing.
 *   A count-based check is wrong on wide desktops (3 items fit, but threshold
 *   was 4 → arrows hidden) and on narrow ones (2 items overflow at 320px,
 *   but threshold was 4 → arrows hidden when scrolling is needed).
 *
 * What it returns
 *   - canScroll: true iff content extends past the visible track. Use this
 *                to decide whether to render arrows at all.
 *   - atStart:   true iff the first slide is at the left edge. Use this to
 *                disable / dim the prev arrow.
 *   - atEnd:     true iff the last slide's right edge is fully visible.
 *                Use this to disable / dim the next arrow. This stops slick
 *                from advancing past the natural end (the default `infinite:
 *                false` behavior keeps scrolling until the *last slide* is
 *                left-aligned, which looks broken — atEnd fires earlier).
 *   - onBeforeChange: pass this into the slick `beforeChange` setting so the
 *                hook re-measures after each slide change. Without this, the
 *                arrows wouldn't update their disabled state mid-scroll.
 */

type CarouselArrowsState = {
  canScroll: boolean;
  atStart: boolean;
  atEnd: boolean;
};

export type UseCarouselArrowsResult = CarouselArrowsState & {
  onBeforeChange: (current: number, next: number) => void;
};

// react-slick stores its internal state on `slider.innerSlider`, including
// the `list` element (the overflow:hidden viewport). The library doesn't
// expose this in its types, so we reach in via a cast. If a future slick
// release renames or removes `innerSlider`, this returns null and the hook
// degrades to "no arrows ever" rather than throwing.
const getInnerSlider = (slider: Slider | null): { list: HTMLElement } | null =>
  ((slider as unknown) as { innerSlider?: { list: HTMLElement } } | null)
    ?.innerSlider ?? null;

const getTrack = (list: HTMLElement | null): HTMLElement | null =>
  list?.querySelector('.slick-track') ?? null;

// Reads the X-axis pixel offset that slick has applied via CSS transform on
// the track. With `infinite: false` and `variableWidth: true`, this is the
// only reliable way to know "how far have we scrolled" — slick's
// currentSlide index doesn't translate to a pixel offset when slides have
// arbitrary widths. Returns 0 when no transform is set (pre-mount, or first
// position).
const readTranslateX = (track: HTMLElement): number => {
  const transform = window.getComputedStyle(track).transform;
  if (!transform || transform === 'none') return 0;
  if (typeof DOMMatrixReadOnly !== 'undefined') {
    return new DOMMatrixReadOnly(transform).m41;
  }

  // jsdom (used in jest) has no DOMMatrixReadOnly. Parse the matrix string
  // ourselves so unit tests can render the carousel without crashing.
  // `matrix(a, b, c, d, tx, ty)` → tx is index 4
  // `matrix3d(...16 values...)` → tx is index 12
  const match = /matrix(?:3d)?\(([^)]+)\)/.exec(transform);
  if (!match) return 0;
  const parts = match[1].split(',').map((p) => parseFloat(p));
  return parts.length === 6 ? parts[4] : parts[12] ?? 0;
};

export function useCarouselArrows(
  sliderRef: RefObject<Slider | null>,
  itemCount: number
): UseCarouselArrowsResult {
  // Defaults are intentionally pessimistic: arrows hidden, both edges
  // "reached." This way the first paint never shows broken/non-functional
  // arrows; once the post-mount measurement runs we update to truth.
  const [state, setState] = useState<CarouselArrowsState>({
    canScroll: false,
    atStart: true,
    atEnd: true,
  });

  // We coalesce repeated measurement requests through a single
  // requestAnimationFrame and track in-flight setTimeouts so we can clean
  // them up on unmount. Without the cleanup, a slide-change that fires
  // right before the carousel unmounts would leave timeouts queued that
  // call setState on a dead component.
  const rafRef = useRef<number | null>(null);
  const timeoutsRef = useRef<number[]>([]);

  // The single source of truth: read DOM, derive the three booleans, push
  // to state if anything changed (no-op render when nothing did).
  const measure = useCallback(() => {
    const inner = getInnerSlider(sliderRef.current);
    const list = inner?.list ?? null;
    const track = getTrack(list);
    if (!list || !track) return;

    // We use the last slide's right edge — *not* track.scrollWidth — to
    // measure how wide the actual content is. The slide wrappers in this
    // codebase typically have a margin-right (e.g. spacing-5 on Articles)
    // that inflates track.scrollWidth without representing visible content.
    // With 3 cards that fit visually, scrollWidth would say "overflow by
    // 24px" because of the trailing card's margin, even though nothing is
    // hidden. Using offsetLeft + offsetWidth on the last slide gives us the
    // real "where does the content end" boundary.
    const slides = track.querySelectorAll<HTMLElement>('.slick-slide');
    const lastSlide = slides[slides.length - 1] ?? null;
    if (!lastSlide) return;

    const contentRight = lastSlide.offsetLeft + lastSlide.offsetWidth;
    // x = how far the track has scrolled to the left, in CSS pixels (≥ 0).
    const x = -readTranslateX(track);
    // visibleRight = the right edge of the viewport in track-coordinate space.
    const visibleRight = x + list.clientWidth;

    // canScroll: any content lies past the right edge when fully scrolled left.
    // atStart:   we haven't moved yet (x at zero).
    // atEnd:     either nothing to scroll, or the last slide's right edge is
    //            already inside the viewport — i.e. nothing more to reveal.
    //            Important: this fires *before* slick's own end (slick keeps
    //            scrolling until the LAST slide is at the left edge with
    //            empty space to the right, which is what users complained
    //            about pre-hook).
    const canScroll = contentRight - list.clientWidth > 1;
    const atStart = x <= 1;
    const atEnd = !canScroll || visibleRight >= contentRight - 1;

    setState((prev) =>
      prev.canScroll === canScroll &&
      prev.atStart === atStart &&
      prev.atEnd === atEnd
        ? prev
        : { canScroll, atStart, atEnd }
    );
  }, [sliderRef]);

  // Schedule a measurement for the next paint, replacing any already-queued
  // one. This collapses bursts (e.g. multiple ResizeObserver fires during a
  // window drag) into a single measurement per frame.
  const scheduleMeasure = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      measure();
    });
  }, [measure]);

  // Re-measure on mount and every time the consumer's item count changes
  // (e.g. async data arrives, filter applied). Cleanup cancels any pending
  // RAF and any in-flight onBeforeChange follow-up timeouts.
  useEffect(() => {
    scheduleMeasure();
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      timeoutsRef.current.forEach((id) => clearTimeout(id));
      timeoutsRef.current = [];
    };
  }, [scheduleMeasure, itemCount]);

  // Re-measure when the carousel's container resizes — covers viewport
  // resize, sidebar toggle, font load, etc. Falls through silently in any
  // environment without ResizeObserver (some older test setups).
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const inner = getInnerSlider(sliderRef.current);
    const list = inner?.list ?? null;
    if (!list) return;

    const ro = new ResizeObserver(scheduleMeasure);
    ro.observe(list);
    return () => ro.disconnect();
  }, [sliderRef, scheduleMeasure, itemCount]);

  // Wire this into the slick `beforeChange` setting so we re-measure when a
  // slide change happens. We do three measurements: one immediately (so
  // atStart/atEnd update before the user sees a stale disabled state), one
  // mid-animation (50ms), and one after slick's default 500ms transition
  // settles. The IDs are kept so the unmount cleanup can cancel them.
  const onBeforeChange = useCallback(() => {
    scheduleMeasure();
    timeoutsRef.current.push(
      window.setTimeout(scheduleMeasure, 50),
      window.setTimeout(scheduleMeasure, 550)
    );
  }, [scheduleMeasure]);

  return { ...state, onBeforeChange };
}
